import { type RefObject, useEffect } from 'react';
import {
	createStore,
	createActionsHook,
	createStateHook,
	createContainer,
} from 'react-sweet-state';
import { functionWithFF } from '@atlassian/jira-feature-flagging-utils';
import { useIsAiOptInEnabled } from '@atlassian/jira-issue-context-service';
import { useCanOptIn, useIsPremium } from '@atlassian/jira-platform-react-hooks-use-ai-opt-in';
import { useIsAnonymous, useLicensedProducts } from '@atlassian/jira-tenant-context-controller';
import { useUserHasConfluenceAccess } from '../use-has-confluence-access';
import { actions, type Actions } from './actions';
import type { State } from './types';

const Store = createStore<State, Actions>({
	initialState: {
		containerRef: { current: null },
		activePopup: undefined,
		selectedText: undefined,
	},
	actions,
	name: 'highlight-actions-store',
});

export const HighlightActionsContainer = createContainer(Store);

const getTriggerPosition = (state: State) => state.triggerPosition;

export const useTriggerPosition = createStateHook(Store, {
	selector: getTriggerPosition,
});

const getSelectedText = (state: State) => state.selectedText;
export const useSelectedText = createStateHook(Store, {
	selector: getSelectedText,
});

export const useHighlightActions = createActionsHook(Store);

export const useHighlightActionsState = createStateHook(Store);

const getActivePopup = (state: State) => state.activePopup;
export const useActivePopup = createStateHook(Store, {
	selector: getActivePopup,
});

export const useSetHighlightContainerRef = (ref: RefObject<HTMLDivElement | null>) => {
	const { setContainerRef } = useHighlightActions();
	useEffect(() => {
		setContainerRef(ref);
	}, [ref, setContainerRef]);
};

const useIsHighlightEnabledOld = () => {
	const licensedProducts = useLicensedProducts();
	const hasConfluence = !!licensedProducts.confluence;
	const isAnonymous = useIsAnonymous();
	const canOptIn = useCanOptIn();

	const isPremium = useIsPremium();
	const isAiOptInEnabled = useIsAiOptInEnabled();
	const isAIFeatureEnabled = isPremium && isAiOptInEnabled;

	return {
		highlightEnabled: hasConfluence && !isAnonymous && (isAIFeatureEnabled || canOptIn),
	};
};

export const useIsHighlightEnabledNew = () => {
	const hasConfluenceAccess = useUserHasConfluenceAccess().hasAccess;
	const isAnonymous = useIsAnonymous();
	const canOptIn = useCanOptIn();

	const isPremium = useIsPremium();
	const isAiOptInEnabled = useIsAiOptInEnabled();
	const isAIFeatureEnabled = isPremium && isAiOptInEnabled;

	return {
		highlightEnabled: hasConfluenceAccess && !isAnonymous && (isAIFeatureEnabled || canOptIn),
	};
};

export const useIsHighlightEnabled = functionWithFF(
	'issue-view-disable-definition-feature-if-the-user-doesnt-have-confluence-access_inh6g',
	useIsHighlightEnabledNew,
	useIsHighlightEnabledOld,
);
